import React, {useState, useContext, useEffect} from 'react';
import DataTable from '@preflighttech/preflight-tables';
import { Pressable, Text, View } from 'react-native';
import { SELLERS_WITH_COUNT, HIDE_SELLER } from './queries';
import { useMutation } from '@apollo/client';
import SubNav from './SubNav';
import { Link } from 'app/src/utils/routing';
import Filter from 'app/src/elements/Filter';
import { Button } from 'app/src/elements/buttons';
import { ResetSettings } from 'app/src/elements/Pagination/ResetSettings';
import { filterButton } from 'app/src/elements/Pagination/FilterStyles';
import { abbreviations as states} from 'app/src/elements/states';

export const RELEASE_OPTIONS = [
  'Do not auto-generate',
  'Generate on receipt of payment',
  'Generate once the seller is funded'
]

import {
  SellersContainer
} from './elements';
import { LinkContainer, LinkSpace, MainContainer } from '../../elements';
import AuthContext from '../../contexts/AuthContext';

const inputStyle = {
  marginBottom: '10px'
}

const Sellers = ({ scope }) => {
  const { hasPolicy } = useContext(AuthContext);
  const [refetch, setRefetch] = useState();
  const [filterOptions, setFilterOptions] = useState();
  const [pendingReviewCount, setPendingReviewCount] = useState();
  const [statusScope, setStatusScope] = useState(scope || 'active');
  const [filters, setFilters] = useState({});
  const [settings, setSettings] = useState({});
  const queryVariables = {
    filterColumns: [],
    filterNames: [],
    scopes: [statusScope],
  };
  const [hideSeller] = useMutation(
    HIDE_SELLER, { onCompleted: refetch }
  )

  useEffect(() => {
    if (scope !== statusScope) {
      setStatusScope(scope);
    }
  }, [scope]);

  const onHide = (seller) => {
    if(window.confirm(`Hide ${seller.name}?`)) {
      hideSeller({ variables: { id: seller.id }});
    }
  };

  const setFilter = (key, value) => {
    const currentFilters = {...filters};
    currentFilters[key] = value;
    setFilters(currentFilters);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryVariables.filterColumns.push(key);
      queryVariables.filterNames.push(value);
    }
  });

  const columns = [
    {
      key: 'name',
      label: 'Seller',
      sort: 'asc',
      content: ({ entry, value }) => (
        <Link to={`/sellers/${entry.id}`}>{value}</Link>
      )
    },
    {
      key: 'companyName',
      label: 'Company Name',
      content: ({ entry, value }) => (
        <Link to={`/sellers/${entry.id}`}>{value}</Link>
      )
    },
    {
      key: 'street',
      label: 'Address',
      content: (entry) => {
        return entry.entry.street
      }
    },
    {
      key: 'city',
      label: 'City',
      content: (entry) => {
        return entry.entry.city
      }
    },
    {
      key: 'state',
      label: 'State',
      content: (entry) => {
        return entry.entry.state
      }
    },
    {
      key: 'postalCode',
      label: 'Zip',
      content: (entry) => {
        return entry.entry.postalCode.formatted
      }
    },
    {
      key: 'phoneNumber',
      label: 'Phone',
      minViewportWidth: 1000,
      value: (entry) => entry.entry.phoneNumber.formatted,
      content: (entry) => entry.entry.phoneNumber.formatted
    },
    {
      key: 'primaryContactEmail',
      label: 'Email',
      minViewportWidth: 1000,
      value: (entry) => entry.entry.primaryContactEmail,
      content: (entry) => entry.entry.primaryContactEmail
    },
    {
      key: 'assetCount',
      label: 'Asset Count',
      minViewportWidth: 1000,
      sort: 'prevent',
      value: (entry) => entry.entry.assetCount,
      content: (entry) => {
        return entry.entry.assetCount
      }
    },
    {
      key: 'primaryContact',
      label: 'Primary Contact',
      minViewportWidth: 1000,
      value: (entry) => entry.entry.contacts.length ?
        `${entry.entry.contacts[0].firstName} ${entry.entry.contacts[0].lastName}` :
        '',
      content: (entry) => entry.entry.contacts.length ?
        `${entry.entry.contacts[0].firstName} ${entry.entry.contacts[0].lastName}` :
        ''
    },
    {
      key: 'status',
      label: 'Status',
      hidden: !(['pending', 'declined'].includes(statusScope)),
    },
    {
      key: 'functions',
      label: 'Functions',
      sort: 'prevent',
      content: (entry) => {
        return (
          <LinkContainer>
            <Link
              to={`/sellers/${entry.entry.id}`}
              style={{marginLeft: '10px'}}
            >
              {'pending' === entry.status ? 'View/Edit' : 'Review'}
            </Link>
            <LinkSpace/>
            {hasPolicy({ type: 'Seller', action: 'hide'}) && (
              <Pressable
                onPress={() => onHide(entry.entry)}
              >
                <Text>Hide</Text>
              </Pressable>
            )}
          </LinkContainer>
        )
      }
    }
  ]

  const buttons = (
    <View style={{display: 'flex', flexDirection: 'row'}}>
      {'pending' === statusScope ? (
        <Button
          style={filterButton}
          label={`Show approved sellers`}
          onPress={() => setStatusScope('active')}
        />
      ) : (
        <Button
          style={filterButton}
          label={
            pendingReviewCount ?
              `Show ${pendingReviewCount} sellers pending review` :
              'Show pending sellers'
          }
          onPress={() => setStatusScope('pending')}
        />
      )}
      <Filter
        clearFilters={() => {
          setFilters({})
          setShowModal(false)
        }}
        filters={filters}
        setFilters={setFilters}
      >
        <View style={{padding: '20px', justifyContent: 'space-between'}}>
          <Text>Seller Name:</Text>
          <input
            type={'text'}
            name="name"
            onChange={e => setFilter('name', e.target.value)}
            value={filters.name || ''}
            style={inputStyle}
          />
          <Text>Address:</Text>
          <input
            type={'text'}
            name="Street"
            onChange={e => setFilter('street', e.target.value)}
            value={filters.street || ''}
            style={inputStyle}
          />
          <Text>City:</Text>
          <input
            type={'text'}
            name="City"
            onChange={e => setFilter('city', e.target.value)}
            value={filters.city || ''}
            style={inputStyle}
          />
          <Text>State:</Text>
          <select
            type={'select'}
            name="state"
            style={inputStyle}
            onChange={e => setFilter('state', e.target.value)}
            value={filters.state || ''}
          >
            <option key={`state-all`}>
              {'All'}
            </option>
            {states.map(state =>
              <option key={`state-${state}`}>
                {state}
              </option>
            )}
          </select>
          <Text>Postal Code:</Text>
          <input
            type={'postal code'}
            name="postal code"
            onChange={e => setFilter('zip', e.target.value)}
            value={filters.zip || ''}
            style={inputStyle}
          ></input>
        </View>
      </Filter>
      <ResetSettings />
    </View>
  );
  return (
    <MainContainer>
      <SubNav refetch={refetch} />
      <SellersContainer>
        <DataTable.Gql
          setRefetch={setRefetch}
          query={SELLERS_WITH_COUNT}
          queryName='sellersWithCount'
          columns={columns}
          buttons={buttons}
          lengthMenu={ [10, 25, 50, 'All'] }
          setSettings={setSettings}
          dataUpdated={data => {
            setPendingReviewCount(data?.sellersWithCount.pendingReviewCount);
            setFilterOptions(data?.sellersWithCount);
          }}
          htmlTable
          queryVariables={queryVariables}
        />
      </SellersContainer>
    </MainContainer>
  )
}

export default Sellers;
